/* eslint-disable no-await-in-loop */
import * as Sentry from '@sentry/react';
import PQueue from 'p-queue';

import { BFF_API_URL } from '../const';

const IPFS_GATEWAYS_WITH_CID = [
  'https://gateway.pinata.cloud/ipfs/{cid}',
  'https://ipfs.io/ipfs/{cid}',
  'https://w3s.link/ipfs/{cid}',
  'https://{cid}.ipfs.dweb.link/',
];

export type FetchIPFSOptions = {
  timeout?: number;
  parseAs?: 'json' | 'text' | 'blob';
  isBFFApi?: boolean;
};

export const defaultOptions: FetchIPFSOptions = {
  timeout: 5000,
  parseAs: 'json',
};

export async function getPublicIPFSData<T>(
  cid: string,
  options: FetchIPFSOptions = defaultOptions,
): Promise<T> {
  const {
    timeout,
    parseAs,
    isBFFApi = true,
  } = {
    ...defaultOptions,
    ...options,
  };

  // If BFF is enabled, try all gateways via BFF first
  if (isBFFApi) {
    for (const gateway of IPFS_GATEWAYS_WITH_CID) {
      const gatewayUrl = gateway.replace('{cid}', cid);
      const url = `${BFF_API_URL}?url=${encodeURIComponent(gatewayUrl)}`;

      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeout);

        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(timer);

        if (!res.ok) {
          // eslint-disable-next-line no-console
          console.warn(`[IPFS] BFF Gateway ${gateway} returned ${res.status}`);
          // eslint-disable-next-line no-continue
          continue;
        }

        switch (parseAs) {
          case 'text':
            return (await res.text()) as T;
          case 'blob':
            return (await res.blob()) as T;
          case 'json':
          default:
            return await res.json();
        }
      } catch (err) {
        Sentry.withScope(scope => {
          scope.setExtra('gateway', gateway);
          scope.setExtra('isBFF', true);
          scope.setLevel('warning');
          Sentry.captureMessage('[IPFS] BFF Gateway failed');
        });
      }
    }

    // If all BFF gateways failed, fallback to direct access
    // eslint-disable-next-line no-console
    console.warn('[IPFS] All BFF gateways failed, trying direct access');
  }

  // Try direct access (either when BFF is off, or as fallback)
  for (const gateway of IPFS_GATEWAYS_WITH_CID) {
    const gatewayUrl = gateway.replace('{cid}', cid);

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);

      // eslint-disable-next-line no-await-in-loop
      const res = await fetch(gatewayUrl, { signal: controller.signal });
      clearTimeout(timer);

      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.warn(`[IPFS] Gateway ${gateway} returned ${res.status}`);
        // eslint-disable-next-line no-continue
        continue;
      }

      switch (parseAs) {
        case 'text':
          return (await res.text()) as T;
        case 'blob':
          return (await res.blob()) as T;
        case 'json':
        default:
          return await res.json();
      }
    } catch (err) {
      Sentry.withScope(scope => {
        scope.setExtra('gateway', gateway);
        scope.setLevel('warning');
        Sentry.captureMessage('[IPFS] Gateway failed');
      });
    }
  }

  const error = new Error(`All IPFS gateways failed`);

  Sentry.withScope(scope => {
    scope.setExtra('cid', cid);
    Sentry.captureException(error);
  });

  throw error;
}

export function initGetPublicIPFSData() {
  const localQueue = new PQueue({
    concurrency: 1,
    interval: 100,
    intervalCap: 1,
    autoStart: true,
  });

  return <T>(
    cid: string,
    options: FetchIPFSOptions = defaultOptions,
  ): Promise<T> =>
    localQueue.add(() => getPublicIPFSData<T>(cid, options)) as Promise<T>;
}
